import def from "csi/def";
import locale from "csi/locale";
import reflection from "csi/reflection";
import { cnl } from "csi/typings";
import { action, computed, makeObservable, observable } from "mobx";
import React from "react";
import { getLocationForUrl, LOCATION_SECTION, LOCATION_SECTION_ITEM } from "csi/utility/routerUtility";
import { getViewAccessRights } from "./viewUtils";
import { View } from "csi/def/View";
import { getCustomViewContainerKeyForType } from "csi/model/customView";

// Routes within data setup:
//    Section URL: #URL=<DataSetupURL>&Section=DataSetupServices
//    Item URL: #URL=<DataSetupURL>&SectionItem=<Type>(_<Subtype>)
//    Instance URL: #URL=<InstanceURL>&Tab=<InstanceViewId>

export const GROUP_GENERAL = 0;
export const GROUP_SERVICES = 1;

export enum ViewURL {
	URL_APPAREL = "centric://APPAREL/Views",
	URL_CALENDAR = "centric://CALENDAR/CalendarDescriptor/Global",
	URL_DATA = "centric://REFLECTION/INSTANCE/_CS_Data/null",
	URL_LIB_SUPPLIERITEM = "centric://REFLECTION/INSTANCE/SiteLibSupplierItem/SiteLibSupplierItem",
	URL_QUALITY = "centric://QUALITY/Views",
	URL_QUALITY_ADMIN = "centric://QUALITY/QualityAdmin",
	URL_QUESTION_SECTION = "centric://QUALITY/QuestionSection/Factory",
	URL_QUEUES_SERVICES = "centric://REFLECTION/INSTANCE/_CS_QueuesAndServices/null",
	URL_SIZE_CHART = "centric://APPAREL/SizeChartAdmin",
	URL_SOURCING = "centric://REFLECTION/INSTANCE/LibSourcing/SiteLibrary",
	URL_SOURCING_VIEW = "centric://REFLECTION/INSTANCE/SourcingViews/null",
	URL_WBS_ADMIN = "centric://REFLECTION/INSTANCE/WbsAdmin/BLANK",
}

export const SUBTYPE_TYPES: Record<string, boolean> = {
	LookupItem: true,
	SpecificationSectionDefinition: true,
	SpecificationItemDefinition: true,
};

export const URL_TYPE_MAP: Record<string, string> = {
	[ViewURL.URL_APPAREL]: "_CS_ApparelViews",
	[ViewURL.URL_CALENDAR]: "CalendarDescriptor",
	[ViewURL.URL_DATA]: "_CS_Data",
	[ViewURL.URL_LIB_SUPPLIERITEM]: "SiteLibSupplierItem",
	[ViewURL.URL_QUALITY]: "_CS_QualityViews",
	[ViewURL.URL_QUALITY_ADMIN]: "QualityAdmin",
	[ViewURL.URL_QUESTION_SECTION]: "QuestionSection",
	[ViewURL.URL_QUEUES_SERVICES]: "_CS_QueuesAndServices",
	[ViewURL.URL_SIZE_CHART]: "SizeChartAdmin",
	[ViewURL.URL_SOURCING]: "LibSourcing",
	[ViewURL.URL_SOURCING_VIEW]: "SourcingViews",
	[ViewURL.URL_WBS_ADMIN]: "WbsAdmin",
};

const disableGalleryViewMap: Record<string, boolean> = {
	SizeChartTemplates: true,
};

export enum DataSetupSectionId {
	GENERAL = "DataSetupGeneral",
	HIERARCHY = "DataSetupHierarchy",
	SUSTAINABILITY = "DataSetupSustainability",
	SPECIFICATION = "DataSetupSpecification",
	QUALITY = "DataSetupQuality",
	SALES = "DataSetupSales",
	SOURCING = "DataSetupSourcing",
	SCHEDULE = "DataSetupSchedule",
	SERVICES = "DataSetupServices",
	PROOFING = "DataSetupProofing",
	VIPBOARDS = "DataSetupVIPBoards",
	PRODUCTCOMPLIANCE = "DataSetupProductCompliance",

	// special value to unify search results
	RELATED = "DataSetupRelatedLinks",
}

export function isGalleryViewDisabledFor(viewId: string): boolean {
	return disableGalleryViewMap[viewId];
}

export function getItemId(nodeType: string, subtypeName?: string): string {
	return SUBTYPE_TYPES[nodeType] ? `${nodeType}_${subtypeName}` : nodeType;
}

export class DataSetupItem {
	/** unique identifier for item (combination of nodeType and subType) */
	readonly id: string;

	/** node type displayed in view and unique identifier for item */
	readonly nodeType: string;

	/** related links to item ids */
	readonly related?: string[];

	/** subtype url for node types that support subtypes */
	readonly subtypeUrl?: cnl;

	/** the name of the node that support subtypes */
	readonly subtypeName?: string;

	/** view root node URL */
	readonly url: cnl;

	/** view identifier */
	readonly viewId: string;

	/** is item available to user */
	get available(): boolean {
		const { subtypeName, viewId, viewOwnerType, viewExists } = this;
		return !!getViewAccessRights(viewOwnerType == reflection.TYPE_QUEUES_SERVICES ? viewOwnerType : reflection.TYPE_DATA_SETUP, viewOwnerType, viewId, subtypeName) && viewExists;
	}

	/** localized description for item */
	get description(): string {
		const { subtypeUrl } = this;

		if (subtypeUrl) {
			const desc = reflection.getSubtypeSetting(subtypeUrl, "SetupDescription");
			const localeName = locale.getLocale();
			return desc[localeName];
		} else {
			return locale.resource("DataSetupItem" + this.nodeType);
		}
	}

	/** browser URL to link to item */
	get href(): string {
		return getLocationForUrl(reflection.URL_DATA_SETUP, { [LOCATION_SECTION_ITEM]: this.id });
	}

	/** name of item */
	get name(): string {
		const {
			subtypeName,
			subtypeUrl,
			viewId,
			viewOwnerType,
		} = this;

		return subtypeUrl ? subtypeName : locale.typeView(viewOwnerType, viewId);
	}

	/** indicate whether or not this view exists, because the module contains this view might not available */
	get viewDefinition(): View {
		const { viewId, viewOwnerType } = this;
		return def.getClassViewDef(reflection.getClass(viewOwnerType), viewId) as View;
	}

	/** the owner type of view */
	get viewOwnerType(): string {
		return URL_TYPE_MAP[this.url];
	}

	/** indicate whether or not this view exists, because the module contains this view might not available */
	get viewExists(): boolean {
		return !!this.viewDefinition;
	}

	/** extract and return the pathHints from a view */
	get pathHintsName(): string | undefined {
		const {
			viewId,
			viewOwnerType,
		} = this;
		const sourceClass = reflection.getClass(viewOwnerType);

		const view = def.getClassViewDef(sourceClass, viewId);

		if (view) {
			const tableView = view as View;
			return tableView.getPageRelationship()?.pathHints;
		}
	}

	/** construct the prefKey based on DataSetupItem attributes */
	get prefKey(): string {
		const {
			nodeType,
			pathHintsName,
			subtypeName,
		} = this;
		return getCustomViewContainerKeyForType(nodeType, subtypeName, pathHintsName);
	}

	/**
	 * Add item into given array
	 */
	gatherItems(items: DataSetupItem[]): void {
		items.push(this);
	}

	/**
	 * Return true if item's name matches the search text
	 */
	matchesSearchText(searchText: string): boolean {
		return this.name.toLocaleLowerCase().includes(searchText.toLocaleLowerCase());
	}

	constructor(nodeType: string, url: cnl, viewId: string, related?: string[], subtypeUrl?: cnl, subtypeName?: string) {
		this.id = getItemId(nodeType, subtypeName);
		this.nodeType = nodeType;
		this.related = related;
		this.subtypeUrl = subtypeUrl;
		this.subtypeName = subtypeName;
		this.url = url;
		this.viewId = viewId;
	}
}

export class DataSetupGroup {
	/**
	 * localization resource for group; if empty, then no name will be shown for group, but group
	 * is needed to properly render section hierarchy
	 */
	readonly id: string;

	/** data setup items in group */
	readonly items: DataSetupItem[];

	/** the resource id of the placeholder */
	readonly placeholderId: string;

	/** name of group */
	get name(): string {
		return locale.resource(this.id);
	}

	/** text of placeholder */
	get placeholderName(): string {
		return locale.resource(this.placeholderId);
	}

	/**
	 * Add group to results if it contains available items
	 */
	addToAvailable(results: DataSetupSectionChild[]): void {
		const { id, items } = this;
		const availableItems = items.filter((item) => item.available);
		const n = availableItems.length;

		if (n === items.length) {
			results.push(this);
		} else if (n) {
			results.push(new DataSetupGroup(id, availableItems));
		}
	}

	/**
	 * Return group containing item with given id
	 */
	findGroupWithItem(itemId: string): DataSetupGroup {
		if (this.items.find((item) => itemId === item.id)) {
			return this;
		}
	}

	/**
	 * Add items in children to given array
	 */
	gatherItems(items: DataSetupItem[]): void {
		this.items.forEach((item) => item.gatherItems(items));
	}

	constructor(id: string, items: DataSetupItem[], placeholderId?: string) {
		this.id = id;
		this.items = items;
		this.placeholderId = placeholderId;
	}
}

export class DataSetupSubsection {
	/** localization resource for subsection */
	readonly id: string;

	/** data setup group and items in subsection */
	readonly groups: DataSetupGroup[];

	/** name of group */
	get name(): string {
		return locale.resource(this.id);
	}

	/**
	 * If any available groups, then add them and subsection to results
	 */
	addToAvailable(results: DataSetupSectionChild[]): void {
		const availableGroups: DataSetupGroup[] = [];

		this.groups.forEach((group) => group.addToAvailable(availableGroups));

		if (availableGroups.length) {
			results.push(this);
			results.push(...availableGroups);
		}
	}

	/**
	 * Return group containing item with given id
	 */
	findGroupWithItem(itemId: string): DataSetupGroup {
		return this.groups.find((group) => !!group.findGroupWithItem(itemId));
	}

	/**
	 * Add items in children to given array
	 */
	gatherItems(items: DataSetupItem[]): void {
		this.groups.forEach((item) => item.gatherItems(items));
	}

	constructor(id: string, groups: DataSetupGroup[]) {
		this.id = id;
		this.groups = groups.filter(Boolean);
	}
}

export type DataSetupSectionChild = DataSetupSubsection | DataSetupGroup;

export class DataSetupSection {
	/** section unique identifier */
	readonly id: DataSetupSectionId;

	/** section content */
	readonly children: DataSetupSectionChild[];

	/** items in section - stored separately for quicker iteration */
	readonly items: DataSetupItem[];

	/** group in section for search */
	readonly groupMap: Record<string, DataSetupGroup> = {};

	/** identifier for section group; only sections with same group id will be searched */
	readonly sectionGroupId: number = GROUP_GENERAL;

	/** indicates if section is available to the user */
	get available(): boolean {
		return this.items.some((item) => item.available);
	}

	/**
	 * list of children (subsections, groups) that contain available items; subsections and groups will
	 * appear together in flattened array rather than have groups under subsections for easier rendering
	 */
	get availableChildren(): DataSetupSectionChild[] {
		return this.children.reduce((memo, child) => {
			child.addToAvailable(memo);
			return memo;
		}, []);
	}

	/** list of available items in the section */
	get availableItems(): DataSetupItem[] {
		return this.items.filter((item) => item.available);
	}

	/** browser URL to link to section */
	get href(): string {
		return getLocationForUrl(reflection.URL_DATA_SETUP, { [LOCATION_SECTION]: this.id });
	}

	/** name of section */
	get name(): string {
		return locale.resource(this.id);
	}

	/**
	 * Return item with given id in section
	 */
	findItem(id: string): DataSetupItem {
		return this.items.find((item) => item.id === id);
	}

	/**
	 * Return group containing item with given id
	 */
	findGroupWithItem(itemId: string): DataSetupGroup {
		let group: DataSetupGroup;

		this.children.some((child) => {
			group = child.findGroupWithItem(itemId);
			return !!group;
		});

		return group;
	}

	/**
	 * Return new section with all available items that match the given search text
	 */
	search(activeItem: DataSetupItem, searchText: string): DataSetupSection {
		const items = this.items.filter((item) => item.available && item.matchesSearchText(searchText));

		// handle the item first, group them by group id
		const itemsByGroupID: Record<string, DataSetupItem[]> = {};
		items.forEach((item) => {
			const group = this.findGroupWithItem(item.id);
			const { id } = group;
			itemsByGroupID[id] = [...(itemsByGroupID[id] || []), item];
		});

		const finalGroup: Record<string, DataSetupGroup> = {};
		for (const [key, value] of Object.entries(itemsByGroupID)) {
			finalGroup[key] = new DataSetupGroup(key, value);
		}

		// handle the groups, include the group as well
		const formattedSearchText = searchText.toLocaleLowerCase();
		for (const [groupName, group] of Object.entries(this.groupMap)) {
			const { id } = group;
			if (groupName.toLocaleLowerCase().includes(formattedSearchText)) {
				finalGroup[id] = new DataSetupGroup(id, group.items);
			}
		}

		const results = Object.values(finalGroup);
		return results.length == 0 ? undefined : new DataSetupSection(this.id, results);
	}

	constructor(id: DataSetupSectionId, children: DataSetupSectionChild[], groupId?: number) {
		this.id = id;
		this.children = children.filter((x) => x != null);
		this.items = [];

		if (groupId) {
			this.sectionGroupId = groupId;
		}

		// compile list of all items in section for quick reference
		this.children.forEach((item) => item.gatherItems(this.items));

		// initial the group map for quick reference
		for (const child of this.availableChildren?.values()) {
			if (child instanceof DataSetupGroup) {
				if (child.id) {
					this.groupMap[child.name] = child;
				}
			} else if (child instanceof DataSetupSubsection) {
				const subsection = child as DataSetupSubsection;
				subsection.groups.forEach((group) => {
					if (group.id) {
						this.groupMap[group.name] = group;
					}
				});
			}
		}
	}
}

export default class DataSetupStore {
	/** sections in data store */
	sections: DataSetupSection[];

	/** item referenced in route */
	@observable.ref private _activeItem: DataSetupItem;

	/** section referenced in route */
	@observable.ref private _activeSection: DataSetupSection;

	/** indicates if search field is focused */
	@observable private _searchFocused: boolean;

	/** id of selected item in search results */
	@observable.ref private _searchSelectedItem?: DataSetupItem;

	/** text entered into search field */
	@observable private _searchText: string = "";

	/** url of node from where user navigated to an item instance */
	@observable returnUrl: string;

	/** read access to active item */
	get activeItem(): DataSetupItem {
		return this._activeItem;
	}

	/** read access to active section */
	get activeSection(): DataSetupSection {
		return this._activeSection;
	}

	/** available sections that are in the same section group as the active section */
	@computed get activeSections(): DataSetupSection[] {
		const { sectionGroupId } = this._activeSection;
		return this.sections.filter((section) => section.available && (section.sectionGroupId === sectionGroupId));
	}

	/** available sections with at least one visible item for current user */
	get availableSections(): DataSetupSection[] {
		return this.sections.filter((section) => section.available);
	}

	/** read access to search text */
	get searchFocused(): boolean {
		return this._searchFocused;
	}

	/** sections with items that match the current search text; if none match, then section of related links */
	@computed get searchSections(): DataSetupSection[] {
		const { _activeItem, activeSections, searchText } = this;

		if (searchText) {
			// match items in sections with search text; only search in sections with same section group id
			return activeSections.map((section) => section.search(_activeItem, searchText)).filter(Boolean);
		}

		// find section items for related links of active item if available
		if (_activeItem?.related) {
			const items = [];

			for (const id of _activeItem.related) {
				activeSections.some((section) => {
					const item = section.findItem(id);

					if (item && item.available) {
						items.push(item);
					}

					return !!item;
				});
			}

			if (items.length) {
				const group = new DataSetupGroup("", items);
				return [new DataSetupSection(DataSetupSectionId.RELATED, [group])];
			}
		}

		return [];
	}

	/** read access to selected search item */
	get searchSelectedItem(): DataSetupItem {
		return this._searchSelectedItem;
	}

	/** read access to search text */
	get searchText(): string {
		return this._searchText;
	}

	/** service section if available to user; otherwise null */
	get serviceSection(): DataSetupSection {
		return this.availableSections.find((section) => section.id === DataSetupSectionId.SERVICES);
	}

	/** setup sections that are available to user */
	get setupSections(): DataSetupSection[] {
		return this.availableSections.filter((section) => section.id !== DataSetupSectionId.SERVICES);
	}

	/** return section that manages the given node type */
	getSectionAndItemForType = (nodeType: string, subtypeName?: string): [section: DataSetupSection, item: DataSetupItem] => {
		const id = getItemId(nodeType, subtypeName);
		const section = this.sections.find((section) => section.findItem(id));
		const item = section?.findItem(id);
		return [section, item];
	};

	/**
	 * Select next or previous search result based on currently selected item
	 * @param up direction of next result
	 */
	@action selectNextSearchResult = (up = false): void => {
		const { searchSections, _searchSelectedItem } = this;
		const n = searchSections.length;

		if (!n) {
			return;
		}

		if (!_searchSelectedItem) {
			if (up) {
				// search last item
				const sectionItems = searchSections[n - 1].items;
				this._searchSelectedItem = sectionItems[sectionItems.length - 1];
			} else {
				// select first item
				this._searchSelectedItem = searchSections[0].items[0];
			}

			return;
		}

		let lastItem: DataSetupItem;
		let next = false;

		for (const section of searchSections) {
			for (const item of section.items) {
				if (next) {
					// reached next item, so select
					this._searchSelectedItem = item;
					return;
				}

				if (item === _searchSelectedItem) {
					if (up) {
						// select previous item if it exists
						if (lastItem) {
							this._searchSelectedItem = lastItem;
						}

						return;
					}

					// if down, then next item should be selected
					next = true;
				}

				lastItem = item;
			}
		}
	};

	/**
	 * Navigate to selected search item
	 */
	@action selectSearchItem = (): void => {
		const { _searchSelectedItem } = this;

		if (_searchSelectedItem) {
			location.href = _searchSelectedItem.href;
		}
	};

	/**
	 * Set search text and ensure current selection still matches
	 */
	@action setSearchText = (text: string): void => {
		this._searchText = text;

		const { searchSections, _searchSelectedItem } = this;
		let id = _searchSelectedItem?.id;

		if (id && !searchSections.some((section) => !!section.findItem(id))) {
			id = this._searchSelectedItem = undefined;
		}

		if (!id) {
			this.selectNextSearchResult();
		}
	};

	/**
	 * Update data setup state with new section and item
	 */
	@action setSectionAndItem = (section: DataSetupSection, item: DataSetupItem): void => {
		// ensure that search state is reset before navigating to a new item; onBlur of the search text
		// field will call this too, but it was happening too late
		this.stopSearch();

		if (this._activeItem !== item) {
			// returnUrl should be reset when item changes; navigating to other instances of the same item (or the table) should not reset it
			this.returnUrl = null;
			this._activeSection = section;
			this._activeItem = item;
		}
	};

	/**
	 * Start search to show related items
	 */
	@action startSearch = (): void => {
		this._searchFocused = true;
		this.selectNextSearchResult();
	};

	/**
	 * Stop search to show selected section and item
	 */
	@action stopSearch = (): void => {
		this._searchFocused = false;
		this._searchSelectedItem = undefined;
		this._searchText = "";
	};

	constructor() {
		makeObservable(this);
	}
}

export const dataSetupStore = new DataSetupStore();
export const DataSetupStoreContext = React.createContext(dataSetupStore);
export function useDataSetupStore(): DataSetupStore {
	return React.useContext(DataSetupStoreContext);
}